<script setup lang="ts">
import { computed } from 'vue';
import { ProgressColor } from '../progress.constants';

type Props = {
  /** Completion percentage. Values outside 0–100 are clamped. */
  progress?: number;
  /** Accent color of the filled bar and its tinted track. */
  color?: ProgressColor;
};

const props = withDefaults(defineProps<Props>(), {
  progress: 0,
  color: ProgressColor.PRIMARY,
});

const colorClass: Record<ProgressColor, string> = {
  [ProgressColor.PRIMARY]: 'bg-primary',
  [ProgressColor.SECONDARY]: 'bg-secondary',
  [ProgressColor.TERTIARY]: 'bg-tertiary',
  [ProgressColor.NEUTRAL]: 'bg-neutral',
  [ProgressColor.SUCCESS]: 'bg-success',
  [ProgressColor.ERROR]: 'bg-error',
};

const clampedProgress = computed(() =>
  Math.round(Math.min(Math.max(props.progress, 0), 100)),
);
</script>

<template>
  <div
    class="relative h-sm w-full overflow-hidden rounded-full"
    role="progressbar"
    aria-valuemin="0"
    aria-valuemax="100"
    :aria-valuenow="clampedProgress"
  >
    <!--
      The track is the accent color dimmed by a 60% surface overlay, matching
      the Figma layer stack, so it stays a tint of the accent under theming.
    -->
    <div class="absolute inset-0" :class="colorClass[color]" />
    <div class="absolute inset-0 bg-surface-container opacity-60" />
    <div
      class="relative h-full rounded-full transition-[width] duration-300 ease-in-out"
      :class="colorClass[color]"
      :style="{ width: `${clampedProgress}%` }"
    />
  </div>
</template>
